/**
 * Service-worker lifecycle + connectivity, as runes.
 *
 * `registerType: 'prompt'` (vite.config) means a new build waits for the user to
 * accept it — `needRefresh` drives the toast. `online` mirrors connectivity so
 * screens can show a quiet offline banner instead of failing silently.
 */
import { registerSW } from 'virtual:pwa-register';

class Pwa {
  needRefresh = $state(false);
  offlineReady = $state(false);
  online = $state(typeof navigator === 'undefined' ? true : navigator.onLine);

  #applyUpdate: (reload?: boolean) => Promise<void> = async () => {};

  init(): void {
    this.#applyUpdate = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        this.needRefresh = true;
      },
      onOfflineReady: () => {
        this.offlineReady = true;
        setTimeout(() => (this.offlineReady = false), 4000);
      },
    });
    addEventListener('online', () => (this.online = true));
    addEventListener('offline', () => (this.online = false));
  }

  reload(): void {
    this.needRefresh = false;
    void this.#applyUpdate(true);
  }

  dismiss(): void {
    this.needRefresh = false;
  }
}

export const pwa = new Pwa();

// --- on-device storage ------------------------------------------------

/** Ask the browser not to evict the cache under storage pressure. Best-effort. */
export async function persistStorage(): Promise<boolean> {
  try {
    return (
      (await navigator.storage?.persisted?.()) || (await navigator.storage?.persist?.()) || false
    );
  } catch {
    return false;
  }
}

export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    if (!e || e.usage == null || e.quota == null) return null;
    return { usage: e.usage, quota: e.quota };
  } catch {
    return null;
  }
}
